import type { AuditFeedback } from '@compass/client/types/audit';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import React from 'react';

import { FormattedDateTime } from '@/components/FormattedDateTime';

interface AuditFeedbackTableProps {
  feedback: AuditFeedback[];
  isLoading?: boolean;
}

const EMPTY_PLACEHOLDER = '—';

const VERDICT_LABEL: Record<string, string> = {
  AI_CORRECT: 'AI was correct',
  HUMAN_CORRECT: 'Human was correct',
  AI_INCORRECT: 'AI was incorrect',
  AI_PARTIAL: 'AI partially correct',
};

function verdictLabel(verdict: string | undefined): string {
  if (!verdict) return EMPTY_PLACEHOLDER;
  return VERDICT_LABEL[verdict.toUpperCase()] ?? verdict;
}

export const AuditFeedbackTable: React.FC<AuditFeedbackTableProps> = ({ feedback, isLoading }) => {
  if (isLoading) {
    return <div className="px-4 py-3 text-sm text-muted-foreground">Loading feedback…</div>;
  }

  if (feedback.length === 0) {
    return (
      <div className="px-4 py-3 text-sm text-muted-foreground">
        No feedback has been submitted for this case yet.
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="absolute left-3 top-0 bottom-0 w-px bg-border" />
      <div className="space-y-6">
        {feedback.map(row => {
          const isAiCorrect = row.verdict?.toUpperCase() === 'AI_CORRECT';
          const Icon = isAiCorrect ? ThumbsUp : ThumbsDown;
          return (
            <div key={row.id} className="relative flex items-start gap-3">
              <div className="relative z-10 shrink-0 h-6 w-6 rounded-full bg-muted flex items-center justify-center border border-border">
                <Icon className="h-3 w-3 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0 pt-0.5 space-y-2">
                <p className="text-sm font-semibold text-foreground break-words whitespace-pre-wrap">
                  {row.feedback || EMPTY_PLACEHOLDER}
                </p>
                {row.verdict && (
                  <p className="text-xs text-foreground">{verdictLabel(row.verdict)}</p>
                )}
                {row.created_at && (
                  <p className="text-[10px] text-muted-foreground">
                    <FormattedDateTime value={row.created_at} />
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
